// Adding a website and its page: details, ownership verification, settings and deletion.
import { CheckCircledIcon, CheckIcon, ChevronDownIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Link,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
  useSearch,
} from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Dialog, DialogTrigger, Form, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';
import {
  ApiError,
  type ChoosableProfile,
  can,
  createSite,
  deleteSite,
  type ExistingProject,
  HARMONISED_STANDARD,
  lookupSite,
  meQuery,
  overviewQuery,
  REGULATORY_PROFILES,
  refreshCardImage,
  requestVerification,
  type Site,
  scansQuery,
  siteQuery,
  sitesQuery,
  updateSite,
  VERIFICATION_METHODS,
  type VerificationMethod,
} from '../api';
import {
  Button,
  buttonClass,
  Choice,
  Field,
  Message,
  Page,
  RequiredNote,
  requestContentFocus,
  useArrivalFocus,
  usePageTitle,
} from '../ui';
import { looksLikeSiteUrl, readForm } from '../util';
import { rightsOf } from './follow-state';
import { FollowSection } from './follow-tab';
import { ScansSection } from './scans';
import { WordPressSection } from './wordpress';

function useProfileOptions() {
  const { t } = useTranslation();
  return REGULATORY_PROFILES.map((value) => ({
    value,
    label: t(`sites.profiles.${value}`),
    hint: t(`sites.profileHints.${value}`),
  }));
}

/** Projects created a moment ago: the first view of their page says so (kept in memory only). */
const justCreated = new Set<string>();

/** What the server said about the address typed (the normalised form and the project that has it). */
interface Checked {
  typed: string;
  url: string | null;
  existing: ExistingProject | null;
}

/**
 * Adding a website creates the project, nothing else: it asks for the address (and, if wanted, a name),
 * starts no analysis, uses none up and asks for neither a law nor the proof of ownership.
 */
export function NewSitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useQuery(meQuery);
  const [address, setAddress] = useState('');
  const [checked, setChecked] = useState<Checked | null>(null);
  /** Shown under the address when it is not a valid web address (blur, submit or the server's answer). */
  const [urlError, setUrlError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // A second submit (Enter pressed twice, a double click) must not send a second request: the state only
  // changes after the next render, this does at once.
  const sending = useRef(false);
  const latestCheck = useRef(0);
  // The latest address for blur: the handler on the field must not close over a stale value.
  const addressRef = useRef(address);
  addressRef.current = address;
  const typed = address.trim();
  const current = checked?.typed === typed ? checked : null;

  /** Asks the server how it reads the address and whether the organisation already has that project. */
  async function check(raw: string): Promise<Checked | null> {
    const value = raw.trim();
    const ticket = ++latestCheck.current;
    if (!value) {
      // Only the latest answer counts: a stale empty check must not clear a newer error.
      if (ticket === latestCheck.current) {
        setChecked(null);
        setUrlError(false);
      }
      return null;
    }
    try {
      const answer = await lookupSite(value);
      const result = { typed: value, url: answer.url, existing: answer.existing };
      if (ticket === latestCheck.current) {
        setChecked(result);
        setUrlError(result.url === null);
      }
      return result;
    } catch {
      // It is only a preview: creating the project checks again.
      return null;
    }
  }

  /** Marks the address as wrong and ignores lookups that were still in flight. */
  function showUrlError(raw: string, focus = true) {
    latestCheck.current += 1;
    const value = raw.trim();
    setChecked({ typed: value, url: null, existing: null });
    setUrlError(true);
    if (focus) document.getElementById('new-site-address')?.focus();
  }

  function reviewAddress(raw: string, focusOnError: boolean) {
    const value = raw.trim();
    if (!value) {
      setChecked(null);
      setUrlError(false);
      return;
    }
    // Obvious mistakes (e.g. «hola») without waiting for the server.
    if (!looksLikeSiteUrl(value)) {
      showUrlError(value, focusOnError);
      return;
    }
    void check(value);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { url = '', name = '' } = readForm(event);
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setError(null);
    try {
      // Refuse an invalid address before creating: the field says how to correct it.
      if (!url.trim() || !looksLikeSiteUrl(url)) {
        showUrlError(url);
        return;
      }
      const preview = current?.typed === url.trim() ? current : await check(url);
      if (preview && preview.url === null) {
        showUrlError(url);
        return;
      }
      if (preview?.existing) return;
      setUrlError(false);
      const site = await createSite({ url, name: name.trim() || undefined });
      justCreated.add(site.id);
      await queryClient.invalidateQueries({ queryKey: sitesQuery.queryKey });
      await navigate({ to: '/sites/$siteId', params: { siteId: site.id } });
    } catch (failure) {
      const code = failure instanceof ApiError ? failure.code : null;
      if (code === 'duplicada') {
        // Somebody (or another click) has just created it: offer to open it.
        if ((await check(url))?.existing == null) setError(t('newSite.errors.duplicada'));
      } else if (code === 'url') {
        showUrlError(url);
      } else {
        setError(code === 'permiso' ? t('newSite.errors.permiso') : t('errors.generic'));
      }
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }

  if (me && !can(me, 'site', 'create')) {
    return (
      <Page title={t('newSite.title')}>
        <Message tone="error">{t('newSite.errors.permiso')}</Message>
        <p className="mt-6">
          <Link to="/">{t('newSite.cancel')}</Link>
        </p>
      </Page>
    );
  }

  const existing = current?.existing ?? null;
  return (
    <Page title={t('newSite.title')}>
      <p className="mb-6 text-ink-muted">{t('newSite.intro')}</p>
      <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-6">
        {error && <Message tone="error">{error}</Message>}
        <RequiredNote />
        <div className="flex flex-col gap-2">
          <Field
            id="new-site-address"
            name="url"
            label={t('newSite.url')}
            description={t('newSite.urlHint')}
            value={address}
            onChange={(value) => {
              setAddress(value);
              setUrlError(false);
            }}
            onBlur={() => reviewAddress(addressRef.current, false)}
            error={urlError ? t('newSite.errors.url') : null}
            inputMode="url"
            autoComplete="url"
            spellCheck="false"
            maxLength={2048}
            isRequired
            markRequired
          />
          <p role="status" className="text-sm text-ink-muted wrap-anywhere">
            {current?.url ? t('newSite.normalized', { url: current.url }) : ''}
          </p>
        </div>
        {existing && (
          <div
            role="status"
            className="flex flex-col gap-3 rounded-md border-l-4 border-brand bg-paper-alt p-3"
          >
            <p>{t('newSite.existing', { name: existing.name })}</p>
            <Link
              to="/sites/$siteId"
              params={{ siteId: existing.id }}
              className={`${buttonClass('primary')} self-start`}
            >
              {t('newSite.open')}
            </Link>
          </div>
        )}
        <Field name="name" label={t('newSite.name')} description={t('newSite.nameHint')} maxLength={120} />
        <div className="flex flex-wrap items-center gap-6">
          {!existing && (
            <Button type="submit" isPending={busy}>
              {busy ? t('newSite.submitting') : t('newSite.submit')}
            </Button>
          )}
          <Link to="/">{t('newSite.cancel')}</Link>
        </div>
      </Form>
    </Page>
  );
}

/** A value to paste somewhere else, with a button that copies it. */
function CopyValue({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-bold">{label}</span>
      <div className="flex flex-wrap items-start gap-2">
        <code className="min-w-0 flex-1 rounded-md bg-paper-alt px-3 py-2 font-mono text-sm break-all">
          {value}
        </code>
        <Button
          variant="secondary"
          onPress={async () => {
            await navigator.clipboard.writeText(value);
            setCopied(true);
          }}
        >
          {copied ? t('site.verification.copied') : t('site.verification.copy')}
          <span className="sr-only"> {label}</span>
        </Button>
      </div>
    </div>
  );
}

function verificationError(t: TFunction, site: Site): string | null {
  const error = site.verification.error;
  if (!error) return null;
  const http = error.match(/^http:(\d+)$/);
  if (http) return t('site.verification.errors.http', { status: http[1] });
  const known = ['meta-missing', 'file-missing', 'dns-missing', 'unreachable', 'timeout'];
  return known.includes(error)
    ? t(`site.verification.errors.${error}`, { url: site.url })
    : t('errors.generic');
}

/** Where the verification is: choose a method, publish the code, check it (4: all done). */
function VerificationSteps({ step }: { step: number }) {
  const { t } = useTranslation();
  const steps = ['choose', 'publish', 'check'] as const;
  return (
    <ol className="verify-steps">
      {steps.map((key, index) => {
        const state = index + 1 < step ? 'done' : index + 1 === step ? 'current' : 'next';
        return (
          <li key={key} data-state={state} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="step-mark" aria-hidden="true">
              {state === 'done' ? <CheckIcon /> : index + 1}
            </span>
            <span>
              {t(`site.verification.steps.${key}`)}
              {state === 'done' && <span className="sr-only"> ({t('site.verification.steps.done')})</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function VerificationSection({ site, mayVerify }: { site: Site; mayVerify: boolean }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  // The server only stores a method after a check; until then «Elige cómo» stays current.
  const savedMethod = site.verification.method;
  const [method, setMethod] = useState<VerificationMethod>(savedMethod ?? 'meta');
  const [picked, setPicked] = useState(savedMethod !== null);
  const [requestError, setRequestError] = useState<string | null>(null);
  // From the click until the API answers: the server status alone would leave the button enabled.
  const [busy, setBusy] = useState(false);
  const { status } = site.verification;
  const pending = busy || status === 'checking';
  useEffect(() => {
    if (savedMethod !== null) setPicked(true);
  }, [savedMethod]);
  const heading = useArrivalFocus();
  // What the live region says: announced when a check starts and when it ends well.
  const [announcement, setAnnouncement] = useState('');
  const previous = useRef(status);
  // When the wait started (click or a check already in flight when the page opened).
  const [checkingSince, setCheckingSince] = useState<number | null>(() =>
    status === 'checking' ? Date.now() : null,
  );
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // A failure is announced by its own alert; success, here.
    if (previous.current === 'checking' && status !== 'checking') {
      setAnnouncement(status === 'verified' ? t('site.verification.done') : '');
      if (!busy) setCheckingSince(null);
    }
    if (status === 'checking' && checkingSince === null) setCheckingSince(Date.now());
    previous.current = status;
  }, [status, t, busy, checkingSince]);
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [pending]);
  const elapsedSec = checkingSince ? Math.floor((now - checkingSince) / 1000) : 0;

  async function check() {
    if (busy || status === 'checking') return;
    setRequestError(null);
    setBusy(true);
    setCheckingSince(Date.now());
    setAnnouncement(t('site.verification.checking'));
    try {
      const updated = await requestVerification(site.id, method);
      queryClient.setQueryData(siteQuery(site.id).queryKey, updated);
    } catch (failure) {
      setCheckingSince(null);
      setAnnouncement('');
      setRequestError(
        failure instanceof ApiError && failure.code === 'limite'
          ? t('site.verification.errors.limite')
          : t('errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  const methods = VERIFICATION_METHODS.map((value) => ({
    value,
    label: t(`site.verification.methods.${value}`),
    hint: t(`site.verification.methodHints.${value}`),
    disabled: pending,
  }));
  const failure = status === 'failed' ? verificationError(t, site) : null;
  const waitHint =
    elapsedSec >= 15
      ? t('site.verification.checkingSlow')
      : elapsedSec >= 5
        ? t('site.verification.checkingWait')
        : t('site.verification.checkingHint');

  return (
    <section aria-labelledby="verificacion" className="mt-10 flex flex-col gap-4">
      <h2 ref={heading} id="verificacion" tabIndex={-1} className="text-2xl font-bold focus:outline-none">
        {t('site.verification.title')}
      </h2>
      <p role="status" className="empty:hidden">
        {announcement}
      </p>
      <VerificationSteps
        step={status === 'verified' ? 4 : pending ? 3 : picked || savedMethod !== null ? 2 : 1}
      />
      {status === 'verified' ? (
        <p role="status" className="verify-done">
          {t('site.verification.verified', {
            date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(
              new Date(site.verification.verifiedAt ?? site.createdAt),
            ),
            method: t(`site.verification.methods.${site.verification.method ?? 'meta'}`),
          })}
        </p>
      ) : (
        <>
          <p>{t('site.verification.intro')}</p>
          <ul className="list-disc pl-5">
            <li>{t('site.verification.without')}</li>
            <li>{t('site.verification.with')}</li>
          </ul>
          {!mayVerify ? (
            <p>{t('site.verification.readOnly')}</p>
          ) : (
            <>
              <Choice
                label={t('site.verification.method')}
                options={methods}
                value={method}
                onChange={(value) => {
                  setMethod(value);
                  setPicked(true);
                }}
              />
              <div className="flex flex-col gap-3 rounded-lg bg-paper-alt/50 p-4">
                {method === 'meta' && (
                  <>
                    <p>{t('site.verification.metaSteps', { url: site.url })}</p>
                    <CopyValue label={t('site.verification.tag')} value={site.verification.meta} />
                  </>
                )}
                {method === 'dns' && (
                  <>
                    <p>{t('site.verification.dnsSteps', { host: site.verification.dns.name })}</p>
                    <CopyValue label={t('site.verification.record')} value={site.verification.dns.value} />
                    <p className="text-sm text-ink-muted">{t('site.verification.dnsNote')}</p>
                  </>
                )}
                {method === 'fichero' && (
                  <>
                    <p>{t('site.verification.fileSteps', { url: site.verification.file.url })}</p>
                    <CopyValue
                      label={t('site.verification.fileContent')}
                      value={site.verification.file.content}
                    />
                  </>
                )}
              </div>
              {failure && !pending && <Message tone="error">{failure}</Message>}
              {requestError && <Message tone="error">{requestError}</Message>}
              <div className="flex flex-col gap-2">
                <Button onPress={() => void check()} isPending={pending} isDisabled={pending}>
                  {pending && <span className="button-spinner" aria-hidden="true" />}
                  {pending ? t('site.verification.checking') : t('site.verification.check')}
                </Button>
                {pending && (
                  <p role="status" className="text-sm text-ink-muted">
                    {waitHint}
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

function SettingsSection({ site }: { site: Site }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const profiles = useProfileOptions();
  const unset = site.regulatoryProfile === 'sin-definir';
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { name = '', regulatoryProfile } = readForm(event);
    setSaved(false);
    setError(null);
    try {
      const updated = await updateSite(site.id, {
        name: name.trim(),
        regulatoryProfile: regulatoryProfile as ChoosableProfile | undefined,
      });
      queryClient.setQueryData(siteQuery(site.id).queryKey, updated);
      await queryClient.invalidateQueries({ queryKey: sitesQuery.queryKey, exact: true });
      setSaved(true);
    } catch (failure) {
      // Saved or not is said clearly: a failure never looks like a confirmation.
      const status = failure instanceof ApiError ? failure.status : 0;
      setError(
        status === 403
          ? t('site.settings.errors.permiso')
          : status === 404
            ? t('site.settings.errors.gone')
            : t('site.settings.errors.generic'),
      );
    }
  }

  return (
    <section aria-labelledby="ajustes" className="mt-10 flex flex-col gap-4">
      <h2 id="ajustes" className="text-2xl font-bold">
        {t('site.settings.title')}
      </h2>
      <p className="max-w-2xl text-ink-muted">
        <Trans
          i18nKey="site.settings.scope"
          components={{
            profile: <Link to="/account/profile" />,
            security: <Link to="/account/security" />,
          }}
        />
      </p>
      <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-6">
        {error && <Message tone="error">{error}</Message>}
        <Field
          name="name"
          label={t('site.settings.name')}
          defaultValue={site.name}
          maxLength={120}
          isRequired
        />
        <Choice
          name="regulatoryProfile"
          label={t('site.profile')}
          description={unset ? t('site.settings.profileUnset') : undefined}
          options={profiles}
          defaultValue={unset ? undefined : (site.regulatoryProfile as ChoosableProfile)}
          // A website without a profile can be renamed without choosing one.
          isRequired={!unset}
        />
        {/* Choosing a profile says which rules are the reference, never that the website meets them. */}
        <p className="max-w-2xl text-sm text-ink-muted">{t('site.settings.profileNote')}</p>
        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit">{t('site.settings.save')}</Button>
          <div role="status" className="font-bold text-ok empty:hidden">
            {saved ? (
              <span className="inline-flex items-center gap-1">
                <CheckCircledIcon aria-hidden="true" />
                {t('site.settings.saved')}
              </span>
            ) : (
              ''
            )}
          </div>
        </div>
      </Form>
      <CardImageSection siteId={site.id} />
    </section>
  );
}

/**
 * Update the home preview picture and icon of the card in «Tus webs». It does not start an analysis
 * and does not use pages of the plan; a new analysis of the home page can also refresh it.
 */
function CardImageSection({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setPending(true);
    setDone(false);
    setError(null);
    try {
      await refreshCardImage(siteId);
      await queryClient.invalidateQueries({ queryKey: overviewQuery.queryKey });
      setDone(true);
    } catch (failure) {
      const status = failure instanceof ApiError ? failure.status : 0;
      setError(
        status === 403
          ? t('site.settings.cardImage.errors.permiso')
          : status === 429
            ? t('site.settings.cardImage.errors.limite')
            : t('site.settings.cardImage.errors.generic'),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-line pt-6">
      <h3 className="text-lg font-bold">{t('site.settings.cardImage.title')}</h3>
      <p className="max-w-2xl whitespace-pre-line text-sm text-ink-muted">
        {t('site.settings.cardImage.intro')}
      </p>
      {error && <Message tone="error">{error}</Message>}
      <div className="flex flex-wrap items-center gap-4">
        <Button onPress={() => void refresh()} isPending={pending} isDisabled={pending}>
          {pending && <span className="button-spinner" aria-hidden="true" />}
          {pending ? t('site.settings.cardImage.working') : t('site.settings.cardImage.action')}
        </Button>
        <div role="status" className="font-bold text-ok empty:hidden">
          {done ? (
            <span className="inline-flex items-center gap-1">
              <CheckCircledIcon aria-hidden="true" />
              {t('site.settings.cardImage.done')}
            </span>
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  );
}

function DeleteSection({ site }: { site: Site }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // The deletion is explicit: the name of the website has to be typed.
  const [typed, setTyped] = useState('');

  async function remove(close: () => void) {
    try {
      await deleteSite(site.id);
      close();
      queryClient.removeQueries({ queryKey: siteQuery(site.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: sitesQuery.queryKey, exact: true });
      await navigate({ to: '/' });
    } catch {
      setError(t('errors.generic'));
    }
  }

  return (
    <section
      aria-labelledby="eliminar"
      className="mt-16 flex flex-col items-start gap-4 rounded-lg border-2 border-error p-5"
    >
      <h2 id="eliminar" className="text-2xl font-bold">
        {t('site.remove.title')}
      </h2>
      <p>{t('site.remove.intro')}</p>
      <p className="font-bold">{t('site.remove.dataTitle')}</p>
      <ul className="list-disc pl-5">
        {(['analyses', 'follow', 'reviews', 'ownership'] as const).map((key) => (
          <li key={key}>{t(`site.remove.data.${key}`)}</li>
        ))}
      </ul>
      <DialogTrigger onOpenChange={() => setTyped('')}>
        <Button variant="danger">{t('site.remove.button')}</Button>
        <ModalOverlay
          isDismissable
          className="fixed inset-0 z-20 flex items-center justify-center bg-black/60 p-4"
        >
          <Modal className="w-full max-w-md rounded-lg border-2 border-line bg-paper p-6 text-ink">
            <Dialog role="alertdialog" className="outline-none">
              {({ close }) => (
                <div className="flex flex-col gap-4">
                  <Heading slot="title" className="text-xl font-bold">
                    {t('site.remove.confirmTitle', { name: site.name })}
                  </Heading>
                  <p>{t('site.remove.intro')}</p>
                  <Field
                    label={t('site.remove.typeName', { name: site.name })}
                    value={typed}
                    onChange={setTyped}
                    autoComplete="off"
                    spellCheck="false"
                  />
                  {error && <Message tone="error">{error}</Message>}
                  <div className="flex flex-wrap justify-end gap-3">
                    <Button variant="secondary" onPress={close} autoFocus>
                      {t('site.remove.cancel')}
                    </Button>
                    <Button
                      variant="danger"
                      isDisabled={typed.trim() !== site.name.trim()}
                      onPress={() => remove(close)}
                    >
                      {t('site.remove.confirm')}
                    </Button>
                  </div>
                </div>
              )}
            </Dialog>
          </Modal>
        </ModalOverlay>
      </DialogTrigger>
    </section>
  );
}

/** The standard the scans use and the harmonised reference, apart so that they are not confused. */
function SiteDetails({ site }: { site: Site }) {
  const { t } = useTranslation();
  const standard = (id: string) => t(`sites.standards.${id}`, { defaultValue: id });
  return (
    <details className="site-details">
      <summary>
        {t('site.details')}
        <ChevronDownIcon aria-hidden="true" />
      </summary>
      <dl>
        <dt>{t('site.address')}</dt>
        <dd className="wrap-anywhere">
          <a href={site.url}>{site.url}</a>
        </dd>
        <dt>{t('site.profile')}</dt>
        <dd>{t(`sites.profiles.${site.regulatoryProfile}`)}</dd>
        <dt>{t('site.analysisStandard')}</dt>
        <dd>{standard(site.targetStandard)}</dd>
        <dt>{t('site.harmonised')}</dt>
        <dd>{standard(HARMONISED_STANDARD)}</dd>
      </dl>
      <p className="text-sm text-ink-muted">{t('site.standardsNote')}</p>
    </details>
  );
}

/** The verification status in the head of a website's page: a badge that leads to the verification tab. */
function StatusBadge({ site }: { site: Site }) {
  const { t } = useTranslation();
  const { status } = site.verification;
  return (
    <Link
      to="/sites/$siteId/verificacion"
      params={{ siteId: site.id }}
      className="status-badge status-link"
      // Not `data-status`: the router puts its own (`active`) on the link of the current page, which
      // would drop the colour of the ownership state exactly on the verification tab.
      data-ownership={status}
      onClick={requestContentFocus}
    >
      {status === 'verified' ? (
        <CheckCircledIcon aria-hidden="true" />
      ) : status !== 'checking' ? (
        <ExclamationTriangleIcon aria-hidden="true" />
      ) : null}
      {t(`sites.status.${status}`)}
      <span className="sr-only"> ({t('site.verificationLink')})</span>
    </Link>
  );
}

/** Leads to the ownership verification, from a control that stays where it is. */
function useShowVerification(siteId: string) {
  const navigate = useNavigate();
  return () => {
    requestContentFocus();
    void navigate({ to: '/sites/$siteId/verificacion', params: { siteId } });
  };
}

/**
 * A website's page: its head (name, status, domain, what to do about its verification and its details)
 * and, under the tabs, one section at a time. Each tab is an address of its own, so the tabs stay in
 * sight when a scan or a page is open, and the browser's back button works between them.
 */
export function SitePage() {
  const { t } = useTranslation();
  const { siteId } = useParams({ from: '/sites/$siteId' });
  const { data: me } = useQuery(meQuery);
  const site = useQuery({ ...siteQuery(siteId), retry: false });
  const scans = useQuery(scansQuery(siteId));
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Said once, when the project has just been created, while no analysis has been started.
  const [created] = useState(() => justCreated.has(siteId));
  useEffect(() => () => void justCreated.delete(siteId), [siteId]);
  // The tabs scroll sideways on a narrow screen: the one open is kept in view, without moving the page.
  const tabs = useRef<HTMLElement>(null);
  const loaded = !site.isPending && !site.isError;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the tab to show changes with the address.
  useEffect(() => {
    const list = tabs.current;
    const active = list?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!list || !active) return;
    list.scrollLeft = Math.max(0, active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2);
  }, [pathname, loaded]);

  if (site.isPending) {
    return (
      <p role="status" className="mx-auto max-w-5xl px-4 py-10">
        {t('app.loading')}
      </p>
    );
  }
  if (site.isError) {
    const missing = site.error instanceof ApiError && site.error.status === 404;
    return (
      <Page title={missing ? t('notFound.title') : t('errors.title')}>
        <p>{missing ? t('site.notFound') : t('errors.generic')}</p>
        <p className="mt-6">
          <Link to="/">{t('site.back')}</Link>
        </p>
      </Page>
    );
  }

  const data = site.data;
  const mayVerify = can(me, 'site', 'update');
  const maySettings = mayVerify || can(me, 'site', 'delete');
  // The report of a scan belongs to the analysis; the others have their own address.
  const base = `/sites/${siteId}`;
  const here = (section: string) =>
    pathname === `${base}/${section}` || pathname.startsWith(`${base}/${section}/`);
  const onAnalysis = pathname === base || pathname === `${base}/` || here('scans') || here('revision-manual');
  const current = (active: boolean) => (active ? 'page' : undefined);
  const showCreated = created && onAnalysis && scans.data?.length === 0;
  return (
    <Page
      title={data.name}
      titleExtra={
        <>
          <StatusBadge site={data} />
          {showCreated && (
            <span role="status" className="status-badge status-notice" data-tone="ok">
              <CheckCircledIcon aria-hidden="true" />
              {t('site.created')}
            </span>
          )}
        </>
      }
      focusKey={data.id}
      setsDocumentTitle={false}
      fluid
    >
      <SiteDetails site={data} />
      <nav ref={tabs} aria-label={data.name} className="site-tab-list">
        <Link
          to="/sites/$siteId"
          params={{ siteId }}
          activeOptions={{ exact: true }}
          className="site-tab"
          aria-current={current(onAnalysis)}
        >
          {t('scans.title')}
        </Link>
        <Link
          to="/sites/$siteId/paginas"
          params={{ siteId }}
          className="site-tab"
          aria-current={current(here('paginas'))}
        >
          {t('site.tabs.pages')}
        </Link>
        <Link
          to="/sites/$siteId/seguimiento"
          params={{ siteId }}
          className="site-tab"
          aria-current={current(here('seguimiento'))}
        >
          {t('site.tabs.follow')}
        </Link>
        <Link
          to="/sites/$siteId/verificacion"
          params={{ siteId }}
          className="site-tab"
          aria-current={current(here('verificacion'))}
        >
          {t('site.verification.title')}
        </Link>
        <Link
          to="/sites/$siteId/declaracion"
          params={{ siteId }}
          className="site-tab"
          aria-current={current(here('declaracion'))}
        >
          {t('statement.tab')}
        </Link>
        <Link
          to="/sites/$siteId/sello"
          params={{ siteId }}
          className="site-tab"
          aria-current={current(here('sello'))}
        >
          {t('seal.tab')}
        </Link>
        <Link
          to="/sites/$siteId/wordpress"
          params={{ siteId }}
          className="site-tab"
          aria-current={current(here('wordpress'))}
        >
          {t('wordpress.tab.name')}
        </Link>
        {maySettings && (
          <Link
            to="/sites/$siteId/ajustes"
            params={{ siteId }}
            className="site-tab"
            aria-current={current(here('ajustes'))}
          >
            {t('site.settings.title')}
          </Link>
        )}
      </nav>
      <div className="site-tab-panel">
        <Outlet />
      </div>
    </Page>
  );
}

/** The data of the website whose page is open (the head of the page has already loaded it). */
function useOpenSite() {
  const { siteId } = useParams({ from: '/sites/$siteId' });
  return useQuery(siteQuery(siteId)).data;
}

export function AnalysisTab() {
  const { data: me } = useQuery(meQuery);
  const site = useOpenSite();
  const navigate = useNavigate();
  const showVerification = useShowVerification(site?.id ?? '');
  // `?preparar` comes from the report of an analysis that could not be completed: it opens the summary.
  const { preparar, comprobar } = useSearch({ strict: false }) as { preparar?: boolean; comprobar?: string };
  if (!site) return null;
  return (
    <ScansSection
      site={site}
      mayScan={can(me, 'site', 'scan')}
      // The settings tab is only offered to the roles that can change or delete the website.
      mayConfigure={can(me, 'site', 'update') || can(me, 'site', 'delete')}
      onVerify={can(me, 'site', 'update') ? showVerification : undefined}
      autoPrepare={preparar === true && can(me, 'site', 'scan')}
      // The elements whose fix is checked (from «Comprobar la corrección»): the summary says what that analyses.
      check={comprobar?.split(',')}
      onAutoPrepared={() => navigate({ to: '/sites/$siteId', params: { siteId: site.id }, replace: true })}
    />
  );
}

export function FollowTab() {
  const { t } = useTranslation();
  const { data: me } = useQuery(meQuery);
  const site = useOpenSite();
  usePageTitle(t('follow.tab.name'), site?.name);
  if (!site) return null;
  return <FollowSection site={site} rights={rightsOf(me)} />;
}

export function VerificationTab() {
  const { t } = useTranslation();
  const { data: me } = useQuery(meQuery);
  const site = useOpenSite();
  usePageTitle(t('site.verification.title'), site?.name);
  if (!site) return null;
  return <VerificationSection site={site} mayVerify={can(me, 'site', 'update')} />;
}

export function WordPressTab() {
  const { t } = useTranslation();
  const site = useOpenSite();
  usePageTitle(t('wordpress.tab.name'), site?.name);
  if (!site) return null;
  return <WordPressSection siteId={site.id} />;
}

export function SettingsTab() {
  const { t } = useTranslation();
  const { data: me } = useQuery(meQuery);
  const site = useOpenSite();
  usePageTitle(t('site.settings.title'), site?.name);
  if (!site) return null;
  // The tab is only offered to roles that can change or delete the website.
  if (!can(me, 'site', 'update') && !can(me, 'site', 'delete')) {
    return <Navigate to="/sites/$siteId" params={{ siteId: site.id }} replace />;
  }
  return (
    <>
      {can(me, 'site', 'update') && <SettingsSection site={site} />}
      {can(me, 'site', 'delete') && <DeleteSection site={site} />}
    </>
  );
}
